import React, { useState } from "react";
import { WagmiProvider, useAccount } from "wagmi";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import BridgeHomePage from "./components/BridgeHomePage";
import ApplyBridgePage from "./components/ApplyBridgePage";
import AdminListingsPage from "./components/AdminListingsPage";
import AdminPanel from "./components/AdminPanel";
import BillingPage from "./components/BillingPage";
import MaintenancePage from "./components/MaintenancePage";
import DeveloperPage from "./components/DeveloperPage";
import BridgeSupportPage from "./components/BridgeSupportPage";
import AddTokenGuidePage from "./components/AddTokenGuidePage";
import { wagmiConfig } from "./walletConfig";
import "./App.css";
import "./bridge-premium.css";

const queryClient = new QueryClient();

const externalLinks = [
  { label: "Website", url: "https://bescfinancial.com" },
  { label: "Swap", url: "https://bescswap.com" },
  { label: "Stablecoin Bridge", url: "https://bescbridge.com" },
  { label: "Twitter", url: "https://twitter.com/bescllc" },
];

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { address, isConnected } = useAccount();
  const handleLinkClick = () => {
    setMenuOpen(false);
  };
  const adminAddress = (
    process.env.REACT_APP_ADMIN_SIGNER_ADDRESS ||
    process.env.REACT_APP_MINT_CONTROLLER_ADDRESS ||
    ""
  ).toLowerCase();
  const isAdmin = isConnected && address && address.toLowerCase() === adminAddress;

  return (
    <header className="header">
      <div className="header-brand" aria-label="BESC Bridge">
        <button
          type="button"
          className="hamburger md:hidden"
          aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>
      <div className="header-inner">
        <nav
          className={`header-nav ${menuOpen ? "flex" : "hidden"} md:flex flex-col md:flex-row items-center md:items-start`}
        >
          {isAdmin && (
            <>
              <Link to="/admin" className="header-link px-4 py-2 md:py-0" onClick={handleLinkClick}>
                Admin
              </Link>
              <Link to="/admin/listings" className="header-link px-4 py-2 md:py-0" onClick={handleLinkClick}>
                Listings
              </Link>
            </>
          )}
          <Link to="/billing" className="header-link px-4 py-2 md:py-0" onClick={handleLinkClick}>
            Billing &amp; Manage
          </Link>
          <Link to="/support" className="header-link px-4 py-2 md:py-0" onClick={handleLinkClick}>
            Support
          </Link>
          <Link to="/developers" className="header-link px-4 py-2 md:py-0" onClick={handleLinkClick}>
            API
          </Link>
          {externalLinks.map(({ label, url }) => (
            <a
              key={label}
              href={url}
              className="header-link px-4 py-2 md:py-0"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleLinkClick}
            >
              {label}
            </a>
          ))}
        </nav>
      </div>
    </header>
  );
};

const Footer = () => {
  const healthUrl = process.env.REACT_APP_BACKEND_URL
    ? `${process.env.REACT_APP_BACKEND_URL.replace(/\/+$/, "")}/status`
    : "";

  return (
    <footer className="premium-footer">
      <div className="premium-footer__card">
        <div className="premium-footer__top">
          <div className="premium-footer__brand">
            <span className="premium-footer__brand-name">BESC Bridge</span>
            <p className="premium-footer__tagline">
              Premium cross-chain infrastructure.
            </p>
          </div>
          <div className="premium-footer__links">
            <div className="premium-footer__link-row">
              <span className="premium-footer__row-label">Tools</span>
              <Link to="/billing" className="premium-footer__pill">
                Billing &amp; Manage
              </Link>
              <Link to="/support" className="premium-footer__pill">
                Support
              </Link>
              <Link to="/developers" className="premium-footer__pill">
                Developer API
              </Link>
              {healthUrl && (
                <a href={healthUrl} className="premium-footer__pill" target="_blank" rel="noopener noreferrer">
                  Health Status <ArrowUpRight size={11} />
                </a>
              )}
            </div>
            <div className="premium-footer__link-row premium-footer__link-row--eco">
              <span className="premium-footer__row-label">Ecosystem</span>
              {externalLinks.map(({ label, url }) => (
                <a key={label} href={url} className="premium-footer__pill premium-footer__pill--eco" target="_blank" rel="noopener noreferrer">
                  {label} <ArrowUpRight size={11} />
                </a>
              ))}
            </div>
          </div>
        </div>
        <div className="premium-footer__bottom">
          <span className="premium-footer__copy">© {new Date().getFullYear()} BESC Financial. All rights reserved.</span>
          <span className="premium-footer__powered">Powered by <strong>BESC</strong></span>
        </div>
      </div>
    </footer>
  );
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const HomeView = () => <BridgeHomePage />;

const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<HomeView />} />
    <Route path="/apply" element={<ApplyBridgePage />} />
    <Route path="/admin" element={<AdminPanel />} />
    <Route path="/admin/listings" element={<AdminListingsPage />} />
    <Route path="/billing" element={<BillingPage />} />
    <Route path="/support" element={<BridgeSupportPage />} />
    <Route path="/admin/guide" element={<AddTokenGuidePage />} />
    <Route path="/developers" element={<DeveloperPage />} />
    <Route path="*" element={<h1 style={{ textAlign: "center" }}>404 - Page Not Found</h1>} />
  </Routes>
);

const metadata = {
  title: "BESC Token Bridge",
  description: "Bridge wBESC and MONEY cross-chain between Ethereum, BSC, and BESC HyperChain with a 0.10% fee.",
};

const HEALTHCHECK_INTERVAL_MS = 30000;
const HEALTHCHECK_TIMEOUT_MS = 8000;

const App = () => {
  const [backendHealth, setBackendHealth] = React.useState("checking");
  const [isHealthCheckPending, setIsHealthCheckPending] = React.useState(false);
  const [hasPassedInitialHealthcheck, setHasPassedInitialHealthcheck] = React.useState(false);
  const [healthFailureCount, setHealthFailureCount] = React.useState(0);
  const healthCheckInFlight = React.useRef(false);

  const markBackendUnhealthy = React.useCallback(() => {
    setBackendHealth("unhealthy");
    setHealthFailureCount((count) => count + 1);
  }, []);

  const checkBackendHealth = React.useCallback(async () => {
    // Deduplicate: focus + visibilitychange + interval can all fire at once on page load.
    // Without this guard, two concurrent failures would jump healthFailureCount from 0→2
    // in a single tick and flash the maintenance page from a single brief network hiccup.
    if (healthCheckInFlight.current) return false;
    healthCheckInFlight.current = true;

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const apiKey = process.env.REACT_APP_API_KEY;

    if (!backendUrl) {
      markBackendUnhealthy();
      healthCheckInFlight.current = false;
      return false;
    }

    setIsHealthCheckPending(true);

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), HEALTHCHECK_TIMEOUT_MS);

    try {
      const headers = apiKey ? { "x-api-key": apiKey } : {};
      const response = await fetch(`${backendUrl}/health`, {
        method: "GET",
        headers,
        cache: "no-store",
        signal: controller.signal,
      });

      const isHealthy = response.ok;
      if (isHealthy) {
        setBackendHealth("healthy");
        setHealthFailureCount(0);
        setHasPassedInitialHealthcheck(true);
      } else {
        markBackendUnhealthy();
      }
      return isHealthy;
    } catch (error) {
      markBackendUnhealthy();
      return false;
    } finally {
      healthCheckInFlight.current = false;
      setIsHealthCheckPending(false);
      window.clearTimeout(timeoutId);
    }
  }, [markBackendUnhealthy]);

  React.useEffect(() => {
    document.title = metadata.title;
    let descriptionMeta = document.querySelector('meta[name="description"]');
    if (!descriptionMeta) {
      descriptionMeta = document.createElement("meta");
      descriptionMeta.name = "description";
      document.head.appendChild(descriptionMeta);
    }
    descriptionMeta.content = metadata.description;
  }, []);

  React.useEffect(() => {
    checkBackendHealth();

    const intervalId = window.setInterval(() => {
      checkBackendHealth();
    }, HEALTHCHECK_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkBackendHealth();
      }
    };

    window.addEventListener("focus", checkBackendHealth);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", checkBackendHealth);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkBackendHealth]);

  const shouldShowMaintenance = backendHealth !== "healthy" && !hasPassedInitialHealthcheck && healthFailureCount >= 3;

  return (
    <QueryClientProvider client={queryClient}>
      <WagmiProvider config={wagmiConfig}>
        <BrowserRouter>
          <ScrollToTop />
          <div className="app-background">
            <Toaster
              position="top-center"
              toastOptions={{
                duration: 3500,
                style: {
                  background: "rgba(10, 14, 24, 0.95)",
                  color: "#f8f3e7",
                  border: "1px solid rgba(255, 214, 102, 0.24)",
                  boxShadow: "0 24px 60px rgba(0, 0, 0, 0.45)",
                },
              }}
            />
            <Header />
            {!shouldShowMaintenance ? (
              <AppRoutes />
            ) : (
              <MaintenancePage
                mode={backendHealth}
                isChecking={isHealthCheckPending}
                onRetry={checkBackendHealth}
                supportUrl={externalLinks.find((entry) => entry.label === "Support")?.url}
              />
            )}
            <Footer />
          </div>
        </BrowserRouter>
      </WagmiProvider>
    </QueryClientProvider>
  );
};

export default App;
